import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapLegend from './MapLegend';
import MapControls from './MapControls';
import MapFilter from './MapFilter';

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Location {
  id: string;
  name: string;
  lat: number;
  lon: number;
  status: 'active' | 'pending' | 'delivered' | 'cancelled';
  type?: 'order' | 'driver' | 'warehouse' | 'customer';
}

interface TrackingMapProps {
  locations: Location[];
  center?: [number, number];
  zoom?: number;
  showRoute?: boolean;
  realtime?: boolean;
  onLocationFocus?: (location: Location) => void;
  focusedLocationId?: string;
}

const TrackingMap: React.FC<TrackingMapProps> = ({
  locations,
  center = [13.7563, 100.5018], // Bangkok default
  zoom = 6,
  showRoute = false,
  realtime = false,
  onLocationFocus,
  focusedLocationId,
}) => {
  // Thailand bounds: [south, west, north, east]
  const thailandBounds: [[number, number], [number, number]] = [
    [5.6, 97.3],  // Southwest corner
    [20.5, 105.6] // Northeast corner
  ];
  const mapRef = useRef<L.Map | null>(null);
  const [currentLocations, setCurrentLocations] = useState<Location[]>(locations);
  const [showLegend, setShowLegend] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [filteredLocations, setFilteredLocations] = useState<Location[]>(locations);

  // Handle location focus when clicked from sidebar
  useEffect(() => {
    if (focusedLocationId && mapRef.current) {
      const location = locations.find(loc => loc.id === focusedLocationId);
      if (location) {
        mapRef.current.setView([location.lat, location.lon], 10, {
          animate: true,
          duration: 1
        });
        onLocationFocus?.(location);
      }
    }
  }, [focusedLocationId, locations, onLocationFocus]);

  // Real-time location updates
  useEffect(() => {
    if (!realtime) return;

    const interval = setInterval(() => {
      setCurrentLocations(prev =>
        prev.map(loc => ({
          ...loc,
          lat: loc.lat + (Math.random() - 0.5) * 0.0001,
          lon: loc.lon + (Math.random() - 0.5) * 0.0001,
        }))
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [realtime]);

  // Update locations when prop changes
  useEffect(() => {
    setCurrentLocations(locations);
  }, [locations]);

  // Apply filters to locations
  useEffect(() => {
    let filtered = currentLocations;

    Object.entries(selectedFilters).forEach(([groupId, values]) => {
      if (values.length === 0) return;

      if (groupId === 'status') {
        filtered = filtered.filter(loc => values.includes(loc.status));
      }
      if (groupId === 'type') {
        filtered = filtered.filter(loc => values.includes(loc.type || 'order'));
      }
    });

    setFilteredLocations(filtered);
  }, [currentLocations, selectedFilters]);

  const handleFilterChange = (groupId: string, values: string[]) => {
    setSelectedFilters(prev => ({ ...prev, [groupId]: values }));
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleCenterMap = () => {
    if (mapRef.current) {
      mapRef.current.setView(center, zoom);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'delivered':
        return '#22c55e';
      case 'pending':
        return '#eab308';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  };

  const getMarkerIcon = (type?: string) => {
    if (type === 'warehouse') {
      return L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: #8b5cf6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
    }
    if (type === 'driver') {
      return L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: #3b82f6; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
    }
    return new L.Icon.Default();
  };

  // Create route polyline if showRoute is enabled
  const routePositions = filteredLocations
    .filter(loc => loc.status !== 'cancelled')
    .map(loc => [loc.lat, loc.lon] as [number, number]);

  // Legend items
  const legendItems = [
    { type: 'warehouse', label: 'คลังสินค้า (Warehouse)', color: '#8b5cf6' },
    { type: 'driver', label: 'คนขับ (Driver)', color: '#3b82f6' },
    { type: 'order', label: 'คำสั่งซื้อ (Order)', color: '#22c55e' },
    { type: 'customer', label: 'ลูกค้า (Customer)', color: '#f59e0b' },
  ];

  // Filter options
  const filterGroups = [
    {
      id: 'status',
      label: 'สถานะ (Status)',
      type: 'checkbox' as const,
      options: [
        { value: 'active', label: 'กำลังดำเนินการ', count: filteredLocations.filter(l => l.status === 'active').length },
        { value: 'pending', label: 'รอดำเนินการ', count: filteredLocations.filter(l => l.status === 'pending').length },
        { value: 'delivered', label: 'ส่งสำเร็จ', count: filteredLocations.filter(l => l.status === 'delivered').length },
        { value: 'cancelled', label: 'ยกเลิก', count: filteredLocations.filter(l => l.status === 'cancelled').length },
      ],
    },
    {
      id: 'type',
      label: 'ประเภท (Type)',
      type: 'checkbox' as const,
      options: [
        { value: 'warehouse', label: 'คลังสินค้า', count: filteredLocations.filter(l => l.type === 'warehouse').length },
        { value: 'driver', label: 'คนขับ', count: filteredLocations.filter(l => l.type === 'driver').length },
        { value: 'order', label: 'คำสั่งซื้อ', count: filteredLocations.filter(l => l.type === 'order').length },
        { value: 'customer', label: 'ลูกค้า', count: filteredLocations.filter(l => l.type === 'customer').length },
      ],
    },
  ];

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        maxBounds={thailandBounds}
        maxBoundsViscosity={1.0}
        minZoom={5}
        maxZoom={12}
        style={{ height: '100%', width: '100%', borderRadius: '8px' }}
        ref={(map) => {
          if (map) {
            mapRef.current = map;
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {showRoute && routePositions.length > 1 && (
          <Polyline
            positions={routePositions}
            color="#3b82f6"
            weight={3}
            opacity={0.7}
            dashArray="10, 10"
          />
        )}

        {filteredLocations.map((location) => (
          <Marker
            key={location.id}
            position={[location.lat, location.lon]}
            icon={getMarkerIcon(location.type)}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-semibold text-gray-900">{location.name}</h3>
                <p className="text-sm text-gray-600">
                  Lat: {location.lat.toFixed(6)}, Lon: {location.lon.toFixed(6)}
                </p>
                <p className="text-sm text-gray-600">
                  Status: <span style={{ color: getStatusColor(location.status) }}>{location.status}</span>
                </p>
                {location.type && (
                  <p className="text-sm text-gray-600">Type: {location.type}</p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Add circle markers for active locations */}
        {filteredLocations
          .filter(loc => loc.status === 'active' || loc.status === 'pending')
          .map((location) => (
            <CircleMarker
              key={`circle-${location.id}`}
              center={[location.lat, location.lon]}
              radius={50}
              pathOptions={{
                color: getStatusColor(location.status),
                fillColor: getStatusColor(location.status),
                fillOpacity: 0.2,
                weight: 2,
              }}
            />
          ))}
      </MapContainer>

      {/* Map Controls */}
      <MapControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onCenterMap={handleCenterMap}
        onToggleLayers={() => setShowLegend(!showLegend)}
        onToggleFilters={() => setShowFilters(!showFilters)}
        showLayers={showLegend}
        showFilters={showFilters}
      />

      {/* Map Legend */}
      {showLegend && (
        <MapLegend items={legendItems} position="bottomright" />
      )}

      {/* Map Filter */}
      <MapFilter
        filters={filterGroups}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearFilters}
        position="bottomleft"
      />
    </div>
  );
};

export default TrackingMap;
